import React, { useState, useEffect, useCallback, useMemo, memo } from "react";
import { View, StyleSheet, Dimensions } from "react-native";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
} from "@mui/material";
import { useSelector } from "react-redux";
import { LinearGradient } from "expo-linear-gradient";
import moment from "moment";

import {
  ImageComponent,
  InfoComponent,
  SCREEN_IDS,
} from "../components/FullComponent.js";
import { translateText } from "../translations/translationService";
import CleanIncident from "../components/Common/ComponentCleanIncident.js";
import SimplifiedCronometro from "../components/Cronometros/ComponentSimplifiedCronometro.js";
import SimplifiedCronometroIncidencias from "../components/Cronometros/ComponentSimplifiedCronometroIncident.js";
import { getListProblemSolution } from "../services/ConnectDataBase.js";
import { parseDurationToMinutes } from "../services/Functions.js";
import { backgroundColor } from "../constants/Constants.js";
import { TIME_WORK_SCREEN_COLORS } from "../style/colors.js";
import { notify } from "../services/ui/notify";

const { width } = Dimensions.get("window");

const formatDate = (dateObj, format = "DD/MM/YY HH:mm") => {
  if (!dateObj?.date) return "-";
  const momentDate = moment(dateObj.date);
  return momentDate.isValid() ? momentDate.format(format) : "-";
};

const formatDuration = (durationObj) => {
  if (!durationObj?.date) return "-";
  return moment(durationObj.date).format("HH:mm:ss");
};

const processTimeData = (tiempos, listOption = []) => {
  return tiempos.map((item) => {
    const {
      usuario,
      team,
      fecha_ini,
      fecha_fin,
      tipo,
      duracion,
      cantidad_proceso,
      cantidad_incidencia,
      incidencia,
    } = item;

    const baseData = {
      usuario: usuario || "",
      equipo: team || "",
      tipo: tipo || "",
      inicio: formatDate(fecha_ini),
      fin: formatDate(fecha_fin),
      duracion: formatDuration(duracion),
      cantidad_proceso: cantidad_proceso
        ? Number(cantidad_proceso).toFixed(1)
        : "-",
      incidencia: incidencia,
    };

    if (incidencia && listOption.length > 0) {
      const matchingOption = listOption.find(
        (option) => option.incidencia === incidencia
      );

      if (matchingOption) {
        baseData.Problema = matchingOption.descind || "-";
        baseData.Solución = matchingOption.descsol || "-";
        baseData.codProblema = matchingOption.incidencia || "-";
        baseData.codSolucion = matchingOption.solucion || "-";
      }
    }

    if (cantidad_incidencia) {
      baseData.cantidad_incidencia = Number(cantidad_incidencia).toFixed(1);
    }

    return baseData;
  });
};

const TimeWorkScreen = ({ navigation }) => {
  const [listOption, setListOption] = useState([]);
  const [showIncidences, setShowIncidences] = useState(false);

  const cod_team = useSelector((state) => state.user.cod_team);
  const infoData = useSelector((state) => state.infoData.infoData);
  const isTimerRunning = useSelector((state) => state.timer.isTimerRunning);
  const lang = useSelector((state) => state.lang.value);
  const equipo = useSelector((state) => state.user.team);

  const { tiempos, ...data } = infoData;

  const totalIncidencias = useMemo(() => {
    return tiempos.reduce((total, item) => {
      const cantidad = parseFloat(item.cantidad_incidencia || 0);
      return total + (isNaN(cantidad) ? 0 : cantidad);
    }, 0);
  }, [tiempos]);

  const timeWorkData = useMemo(() => {
    const filtered = tiempos.filter(
      ({ tipo_tiempo }) => tipo_tiempo === "TIME_WORK"
    );
    const processed = processTimeData(filtered, listOption);
    return processed.sort((a, b) =>
      moment(b.fin, "DD/MM/YY HH:mm").diff(moment(a.fin, "DD/MM/YY HH:mm"))
    );
  }, [tiempos, listOption]);

  const workIncidences = useMemo(() => {
    const filtered = tiempos.filter(
      ({ tipo_tiempo, incidencia }) => tipo_tiempo === "TIME_WORK" && incidencia
    );
    const processed = processTimeData(filtered, listOption);
    return processed.sort((a, b) =>
      moment(b.fin, "DD/MM/YY HH:mm").diff(moment(a.fin, "DD/MM/YY HH:mm"))
    );
  }, [tiempos, listOption]);

  const qualityProblems = useMemo(() => {
    const filtered = tiempos.filter(
      ({ tipo_tiempo, incidencia }) =>
        tipo_tiempo === "PROBLEM_QUALITY" && incidencia
    );
    const processed = processTimeData(filtered, listOption);
    return processed.sort((a, b) =>
      moment(b.fin, "DD/MM/YY HH:mm").diff(moment(a.fin, "DD/MM/YY HH:mm"))
    );
  }, [tiempos, listOption]);

  const toggleIncidences = useCallback(() => {
    setShowIncidences((prev) => !prev);
  }, []);

  const getListOptionsProblemQuality = useCallback(async () => {
    try {
      const options = await getListProblemSolution(
        infoData.MO,
        5,
        cod_team,
        "list"
      );
      setListOption(options);
    } catch (error) {
      console.error("Error al obtener opciones:", error);
    }
  }, [infoData.MO, cod_team]);

  useEffect(() => {
    getListOptionsProblemQuality();
  }, [getListOptionsProblemQuality]);

  useEffect(() => {
    const unsubscribe = navigation.addListener("beforeRemove", (e) => {
      if (!isTimerRunning) return;

      e.preventDefault();
      notify.warn("¡Cronómetro activo! Debes pararlo antes de salir.");
    });

    return unsubscribe;
  }, [navigation, isTimerRunning]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!isTimerRunning) return;

      event.preventDefault();
      event.returnValue =
        "Tienes un cronómetro en marcha. ¿Estás seguro de querer salir?";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isTimerRunning]);

  const MainTable = memo(() => {
    const headers = [
      "Operario",
      "Equipo",
      "Tipo",
      "Inicio",
      "Fin",
      "Min. Real",
      "Unidades",
    ];

    return (
      <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
        <Table stickyHeader size='small'>
          <TableHead>
            <TableRow sx={{ backgroundColor: TIME_WORK_SCREEN_COLORS.BACKGROUND_TABLE_HEADER }}>
              {headers.map((header) => (
                <TableCell
                  key={header}
                  align='center'
                  sx={{ backgroundColor: `${TIME_WORK_SCREEN_COLORS.BACKGROUND_TABLE_HEADER} !important` }}>
                  {translateText(header, lang)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {timeWorkData.map((item, index) => (
              <TableRow
                key={`${item.usuario}-${index}`}
                sx={{
                  backgroundColor: item.incidencia ? TIME_WORK_SCREEN_COLORS.STATUS_ERROR : "inherit",
                }}>
                <TableCell align='center'>{item.usuario}</TableCell>
                <TableCell align='center'>{item.equipo}</TableCell>
                <TableCell align='center'>{item.tipo}</TableCell>
                <TableCell align='center'>{item.inicio}</TableCell>
                <TableCell align='center'>{item.fin}</TableCell>
                <TableCell align='center'>
                  {parseDurationToMinutes(item.duracion)?.toFixed(2) || "-"}
                </TableCell>
                <TableCell align='center'>{item.cantidad_proceso}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  });

  const IncidencesTable = memo(() => (
    <Box display='flex' gap={2} sx={{ flex: 1 }}>
      <TableContainer component={Paper} sx={{ flex: 1, maxHeight: 300 }}>
        <Table stickyHeader size='small'>
          <TableHead>
            <TableRow sx={{ backgroundColor: TIME_WORK_SCREEN_COLORS.BACKGROUND_TABLE_HEADER }}>
              {["Problema", "Solución", "Cantidad"].map((header) => (
                <TableCell
                  key={header}
                  align='center'
                  sx={{ backgroundColor: `${TIME_WORK_SCREEN_COLORS.BACKGROUND_TABLE_HEADER} !important` }}>
                  {translateText(header, lang)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {workIncidences.map((item, index) => (
              <TableRow key={`${item.Problema}-${index}`}>
                <TableCell align='center'>{item.Problema || "-"}</TableCell>
                <TableCell align='center'>{item.Solución || "-"}</TableCell>
                <TableCell align='center'>
                  {item.cantidad_incidencia || "-"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TableContainer component={Paper} sx={{ flex: 2, maxHeight: 300 }}>
        <Table stickyHeader size='small'>
          <TableHead>
            <TableRow sx={{ backgroundColor: TIME_WORK_SCREEN_COLORS.BACKGROUND_TABLE_HEADER }}>
              {[
                "Operario",
                "Equipo",
                "Tipo",
                "Inicio",
                "Fin",
                "Min. Real",
                "Unidades",
              ].map((header) => (
                <TableCell
                  key={header}
                  align='center'
                  sx={{ backgroundColor: `${TIME_WORK_SCREEN_COLORS.BACKGROUND_TABLE_HEADER} !important` }}>
                  {translateText(header, lang)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {qualityProblems.map((item, index) => (
              <TableRow key={`quality-${index}`}>
                <TableCell align='center'>{item.usuario}</TableCell>
                <TableCell align='center'>{item.equipo}</TableCell>
                <TableCell align='center'>{item.tipo}</TableCell>
                <TableCell align='center'>{item.inicio}</TableCell>
                <TableCell align='center'>{item.fin}</TableCell>
                <TableCell align='center'>
                  {parseDurationToMinutes(item.duracion)?.toFixed(2) || "-"}
                </TableCell>
                <TableCell align='center'>{item.cantidad_proceso}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  ));

  const ControlPanel = memo(() => (
    <Box display='flex' gap={2} sx={{ marginBottom: 2 }}>
      <Button
        variant='contained'
        color={showIncidences ? "primary" : "error"}
        onClick={toggleIncidences}>
        {translateText(showIncidences ? "Tiempos" : "Incidencias", lang)}
      </Button>

      {showIncidences ? (
        <SimplifiedCronometroIncidencias
          screen={SCREEN_IDS.TIME_WORK}
          listData={data.eFabrica}
          po={data.PO}
          mo={data.MO}
          fulldata={data}
          listIncidencias={workIncidences}
          totalIncidencias={totalIncidencias}
          navigation={navigation}
        />
      ) : (
        <SimplifiedCronometro
          screen={SCREEN_IDS.TIME_WORK}
          listData={data.eFabrica}
          po={data.PO}
          mo={data.MO}
          fulldata={data}
          totalIncidencias={totalIncidencias}
          navigation={navigation}
        />
      )}
    </Box>
  ));

  return (
    <LinearGradient colors={backgroundColor()} style={styles.container}>
      <View style={styles.content}>
        <View style={styles.infoSection}>
          <View style={styles.infoColumn}>
            <InfoComponent data={data} />
          </View>
          <View style={[styles.infoColumn, styles.imageColumn]}>
            <ImageComponent data={data} />
          </View>
        </View>

        <Box sx={{ padding: 2 }}>
          <ControlPanel />

          {equipo === "Control de calidad" && <CleanIncident />}

          <Box sx={{ height: "40vh" }}>
            {showIncidences ? <IncidencesTable /> : <MainTable />}
          </Box>
        </Box>
      </View>
    </LinearGradient>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    flex: 1,
    padding: 10,
  },
  infoSection: {
    flexDirection: "row",
    marginBottom: 10,
  },
  infoColumn: {
    flex: 1,
  },
  imageColumn: {
    marginLeft: 20,
  },
});

export default memo(TimeWorkScreen);
